// Consolidated Capability Statement — integrates DEPP C&M's combined experience
// including alliance capabilities derived from Rodhe Industrial and REX Contra Incendio
// portfolio materials. Organized by sector for institutional / investor audiences.

export const CAPABILITY_STATEMENT = {
  en: {
    hero: {
      eyebrow: "Capability Statement · Portfolio",
      title: "A consolidated track record across engineering, industrial services and life-safety.",
      subtitle:
        "DEPP C&M, operating in alliance with a network of specialized firms, consolidates more than two decades of field-proven experience across oil & gas, refining and petrochemicals, power generation, steel, automotive, ports, healthcare and industrial infrastructure — delivered under international codes and ASME, API, NFPA and FM Global frameworks.",
      cta_back: "Back to site",
      cta_contact: "Request Advisory",
      meta: [
        { k: "Years of practice", v: "18+" },
        { k: "Projects delivered", v: "500+" },
        { k: "Clients served", v: "50+" },
        { k: "Workshop footprint", v: "40,000 m²" },
      ],
    },
    exec: {
      eyebrow: "Executive summary",
      title: "Integrated engineering, construction, inspection and life-safety — under a single accountable partner.",
      body: [
        "DEPP C&M delivers a consolidated capability that spans the full industrial life-cycle: conceptual and detail engineering, procurement, fabrication, field construction, non-destructive inspection, mechanical integrity, commissioning, and long-term maintenance — extended with a dedicated fire-protection and industrial-safety practice.",
        "Our combined track record covers upstream, midstream and downstream oil & gas (PEMEX refinery programs, gas-processing facilities, Dos Bocas Refinery and Minatitlán WOL assignments), refining and petrochemical specialty fabrication (coke drums, spherical tanks, pressure vessels, reformers, heat exchangers), power generation (Iberdrola and Grupo Comego cogeneration plants), heavy industry (Ternium, Tenaris TAMSA), automotive manufacturing (Nissan, Panasonic, Ametek), medical-device manufacturing (ConvaTec), airport and cultural infrastructure, and healthcare facilities.",
        "We operate under international standards — ASME (U, U2, R, S, NB stamps), API (510, 570, 571, 579, 580, 650, 653, 2000), ISO 9001/14001/45001, AWS, ASNT (Levels I–III), DOT 406/407/412/MC33 and NFPA 11/30/780 — and Mexican regulatory frameworks (NRF-053-PEMEX, NOM-022-STPS), bringing institutional-grade governance to every assignment.",
      ],
    },
    capabilities: {
      eyebrow: "Core capabilities",
      title: "Seven integrated practices. One accountable partner.",
      groups: [
        {
          name: "Engineering & Infrastructure",
          items: [
            "Conceptual, basic and detail engineering",
            "Pipeline systems — gas, crude and condensate",
            "Process design · P&IDs · Flow diagrams",
            "Civil works, industrial buildings and urbanization",
            "AS-BUILT engineering, cross-sections and material specifications",
          ],
        },
        {
          name: "Field Operations & Drilling",
          items: [
            "Wellhead installation and maintenance",
            "Valves and pressure-control equipment",
            "Swabbing and artificial-lift systems",
            "Well commissioning and workover support",
            "Rig mobilization and rig-up operations",
          ],
        },
        {
          name: "Offshore Platforms & Vessels",
          items: [
            "Jack-up, semi-submersible and drillship support",
            "Fixed and modular platform maintenance",
            "Offshore commissioning and start-up",
            "Marine logistics and victualling",
          ],
        },
        {
          name: "Industrial Maintenance & Inspection",
          items: [
            "Preventive and corrective maintenance programs",
            "Non-destructive testing (UT · RT · MT · PT) ASNT-certified",
            "Mechanical integrity and risk-based inspection",
            "Plant and equipment turnaround",
            "Mechanical integrity reviews of pressure vessels and columns",
          ],
        },
        {
          name: "Cranes, Rigging & Heavy Lift",
          items: [
            "Land cranes, HIABs and certified forklifts",
            "Heavy-lift maneuvers and rigging engineering",
            "Certified operators and riggers",
            "Nationwide mobilization of spreads and packages",
          ],
        },
        {
          name: "Fabrication & Heavy Equipment",
          items: [
            "Pressure vessels, storage tanks and heat exchangers",
            "Cryogenic systems",
            "Fuel-transport systems",
            "Skid-mounted process packages",
            "ASME U-stamp pressure equipment and QA/QC liberation",
          ],
        },
        {
          name: "Specialty Process Fabrication (Refining & Petrochemicals)",
          items: [
            "Coke drums — design, fabrication and Welding Overlay (WOL) with Inconel 625 via temper-bead process",
            "Spherical tanks (LPG / LNG / CNG) — design, construction and rehabilitation",
            "Vertical storage tanks — fabrication and rehabilitation (API 650 / API 653)",
            "Heat exchangers, reformers, heaters (crude, vacuum, hydrodesulfurization, reformation, ethane)",
            "Autotanker / trailer-tank fabrication — DOT 406/407/412, MC33 — over 4,500 units delivered",
            "Plate rolling up to 4″ thickness · Head pressing up to 1,500 tons · Laser/plasma plate cutting",
            "Robotic welding for overlay · SMAW · SAW · GMAW · GTAW · FCAW · ESW · PAW",
          ],
        },
        {
          name: "Plant Rehabilitation & Process Decontamination",
          items: [
            "Full-plant rehabilitation — diagnostic, repair procedures, static and dynamic equipment",
            "Process decontamination — 60+ projects delivered across Mexican refineries since 2008",
            "Chemical cleaning and Packinox / heat-exchanger efficiency recovery",
            "Hot Tapping and Tapping-Machine services",
            "High-pressure, high-flow recirculation for on-line decontamination",
            "Industrial vacuum services — 3,037 US-gallon / 15-cubic-yard units",
            "Non-metallic ASME PCC-2 reinforcements",
          ],
        },
        {
          name: "Electrical, HVAC & Specialty Coatings",
          items: [
            "Electrical installations for PEMEX facilities",
            "Power transformer installation and preventive maintenance of substations",
            "HVAC chillers, industrial refrigeration and cleanroom reconditioning",
            "Specialty coatings — sand-blast white-metal, RP-4, RE-30A (high-temperature aluminum), 5GP fire-retardant, epoxy zinc-chromate",
            "Waterproofing — hot laminar systems and cold-applied membranes",
          ],
        },
        {
          name: "Fire Protection & Industrial Safety",
          items: [
            "Engineering (NFPA / FM Global) — risk studies, hydraulic and electrical calculations, seismic calculations, SCI isometrics, P&IDs, flow and pump-efficiency tests",
            "Water-based systems — wet · dry · pre-action · deluge · water-foam · antifreeze sprinklers",
            "Clean-agent systems — FM-200, Novec 1230, Ecaro-25, CO₂, Argonite",
            "Fire pumps (up to 2,000 GPM) — diesel engines, electric, jockey — and pump-room construction",
            "Detection and alarm systems — installation, commissioning and corrective service",
            "Underground piping (PVC / HDPE thermofusion) and hydrant flow testing",
            "Kitchen-hood suppression — Amerex and Buckeye systems",
            "Inspection policies — monthly and annual — on extinguishers, fixed systems, detection and alarms",
          ],
        },
        {
          name: "HSE, Compliance & Environmental",
          items: [
            "HSE programs — OSHA, API, ISO and NOM-022-STPS compliance",
            "Oily-water treatment and hydrocarbon recovery",
            "Specialized personnel supply (engineering, field, QA/QC, HSE)",
            "Administration, logistics and victualling for offshore and remote operations",
          ],
        },
      ],
    },
    portfolio: {
      eyebrow: "Project portfolio",
      title: "Selected assignments — organized by sector.",
      subtitle:
        "A representative cross-section of the combined project portfolio across the sectors in which DEPP C&M and its alliance partners have operated. All project names preserved as delivered.",
      sectors: [
        {
          name: "Oil & Gas — Upstream, Midstream & Downstream",
          projects: [
            "PEMEX Stations — Electrical installations for PEMEX facilities",
            "T.A.R. El Castillo Storage Terminal — Guadalajara, Jalisco · Civil works, concrete firm reposition, electrical feeding and monitoring for loading bays",
            "Naphtha Fractionation Plant & Storage — CPG Burgos · RE-30A high-temperature aluminum coating, 3-mil final thickness",
            "Activo Integral Burgos — AS-BUILT engineering, cross-sections and material specifications for mechanical arrangements",
            "Gas Regeneration Tower — Detail engineering, procurement supervision and commissioning of a regeneration column in a gas treatment skid",
            "Amine Treatment Column — Mechanical integrity review, NDT inspection and corrective maintenance plan (H₂S-removal service)",
            "Horizontal Separator — Pressure Vessel — Procurement, fabrication inspection and QA/QC sign-off of a three-phase separator with pressure-control skid",
            "Onshore Drilling Rig Program — Rig mobilization, substructure assembly, drawworks & rotary, derrick assembly, solids control, triplex mud pumps, generator & engine power packages",
            "Offshore Field Base Operations — Gulf of Mexico",
          ],
        },
        {
          name: "Refining & Petrochemical Specialty Fabrication",
          projects: [
            "PEMEX Minatitlán Refinery — Coke Drum V-31001 Welding Overlay (WOL) with Inconel 625 via temper-bead (2–4 layers) — 2016",
            "PEMEX Minatitlán Refinery — Coke Drum V-31002 laser mapping, ovalization and bulge diagnostics",
            "PEMEX Minatitlán Refinery — Coke Drum V-31003 rehabilitation program",
            "PEMEX — Fabrication and delivery of 806 autotankers (tractocamión-tanque) under DOT/SCT specifications",
            "Nueva Refinería Dos Bocas — Fabrication of vertical storage tanks (TV-90400 and associated tank field)",
            "Refinería Cadereyta / Madero / Minatitlán — Coke-drum materials: 1¼ Cr-Mo, 2¼ Cr-Mo with 410 stainless clad",
            "Air Liquide — Pressure vessel engineering and mechanical calculations for an activated-carbon tower",
            "International Epizen — Design and construction of ASME-stamped spherical tanks",
            "Mexican Refineries — 60+ process-decontamination projects since 2008 (Packinox / heat-exchanger efficiency recovery, pyrophoric-fire prevention, atmospheric-contamination control)",
            "Pressure vessels delivered in Hastelloy, Inconel, Nickel alloys, Copper alloys, low-alloy steels, stainless steel, carbon steel and aluminum — including crude fractionators, vacuum units for catalytic converters, amine towers and alkylation towers",
            "Heaters and Reformers (ITT of Italy collaboration) — crude, vacuum, hydrodesulfurization, reformation, coke, ethane; hydrogen, ammonia and methanol reformers",
          ],
        },
        {
          name: "Power Generation",
          projects: [
            "Iberdrola Cogeneration Plant — Ramos Arizpe, Coahuila · Commissioning of detection & alarm system",
            "Iberdrola Cogeneration Plant — Ramos Arizpe · Engineering of Novec 1230 clean-agent system for electronic room, raised floor, control room, meeting room and electronic room",
            "Iberdrola Cogeneration Plant — Ramos Arizpe · Engineering of fire-pumping system",
            "Iberdrola Cogeneration Plant — Ramos Arizpe · CO₂ tank hydrostatic testing and recharge (100-lb)",
            "Comego Central Lomas del Real — Valle Hermoso, Tamaulipas · Deluge maintenance on main transformers, auxiliary transformers, gas turbines TG1/TG2, steam turbine, cable basement and trenches",
            "Comego Central Lomas del Real — Pump-efficiency tests on diesel-electric and jockey pumps; hydrant flow testing",
            "Comego Central Anahuac — Valle Hermoso · Hydrant flow testing",
            "Comego Central Valle Hermoso — Hydrant flow testing",
            "Techgen — Pesquería, Nuevo León · Inspection, testing and maintenance policy for fire-protection systems",
          ],
        },
        {
          name: "Steel & Heavy Industry",
          projects: [
            "Ternium Planta Guerrero — San Nicolás de los Garza, NL · Fire-protection ring closure via HDPE thermofusion after dismantling (MC1)",
            "Ternium Planta Guerrero — Inspection, testing and maintenance policy on monitor nozzles",
            "Ternium México — San Nicolás de los Garza, NL · Repair of 12″ underground fire rings via thermofusion",
            "Tenaris TAMSA (Tubos de Acero de México) — Ciénega de Flores, NL · Repair of 12″ underground fire rings via thermofusion",
            "Tenigal — Pesquería, NL · Fire-protection corrective works; alarm-valve replacement in hazardous-waste warehouse sprinkler system; maintenance policy",
          ],
        },
        {
          name: "Automotive & Manufacturing",
          projects: [
            "Alcom Electrónicos de México — Reynosa · Automatic sprinkler systems across MD1/MD2/MD3 (Stage 1, 2 — 22,500 m², and Stage 3); Riser 2.2, Riser 3, Riser 6; underground PVC tying main ring to pump system; foam-water deluge for chemical warehouse; CO₂ preventive on 11 paint booths; Chiller flow measurement; molding-area sprinklers",
            "Panasonic Automotive Systems Reynosa — Inspection policy and major maintenance of fire pump and combustion engine",
            "Ametek Lamb Motores de México Plant I & II — Reynosa · 2,000-GPM fire pump engineering and installation; sprinklers in Villa Florida warehouse and Power & Switch; internal maintenance of centrifugal fire pump; monthly inspection policy",
            "Motores Herméticos del Sur — Reynosa · Clean-agent system for 7 die-cast cells with cross-zone detection; fire suppression in die-cast cell hoods (cells 3/4/5/9); FM-200 recharge; preventive maintenance of cooling tower, chillers and cisterns",
            "Nissan Mexicana Plant A1 — Aguascalientes · Automatic sprinkler system for vehicle assembly area (6 risers, 2,916 m²)",
            "Monroe México — Reynosa · Annual extinguisher-recharge and maintenance policy",
            "Cornelius Manufacturing de México — Reynosa · Industrial kitchen suppression (Buckeye)",
            "Brunswick Compañías de México — Reynosa · FM-Global mold-building construction; fire protection in foaming room, south roofing area, mold-maintenance area; antifreeze sprinklers in south roof, chemicals room and molding area",
            "Leroy-Somer — Reynosa · Corrective works on 78 detection and alarm faults; restoration to normal operation",
            "Impresora Donneco Internacional — Reynosa · Preventive and corrective works on bolted tank; internal maintenance of centrifugal fire pump",
          ],
        },
        {
          name: "Ports & Maritime Infrastructure",
          projects: [
            "Administración Portuaria Integral de Altamira (API Altamira) — Ecaro-25 clean-agent system for data site",
            "Administración Portuaria Integral de Altamira (API Altamira) — Amerex kitchen-hood suppression",
          ],
        },
        {
          name: "Healthcare & Medical Devices",
          projects: [
            "Unomedical Devices — ConvaTec — Reynosa · 2,000-GPM fire pump engineering and installation; monthly inspection policy on fixed systems and detection/alarms",
            "Hospital Clínica Nova — San Nicolás de los Garza · Detection and alarm system in laboratory, X-ray area and ramps N1/N2/N3",
            "Unidad Médica Familiar Prados de Alcalá — Application of 5GP fire-retardant coating (3-hour rating) on metallic structure",
          ],
        },
        {
          name: "Education & Public Institutions",
          projects: [
            "Universidad Autónoma de Tamaulipas — UAT Victoria · Preventive maintenance of 12 FM-200 systems in Main Library",
          ],
        },
        {
          name: "Aviation & Cultural Infrastructure",
          projects: [
            "New Airport Building — Reynosa · Electrical installations",
            "Centro Cultural Reynosa — Hydro-sanitary installations and mezzanine construction",
          ],
        },
        {
          name: "Industrial Construction (FM-Global compliant)",
          projects: [
            "Mold Building — Parque del Puente, Brunswick, Reynosa · Supervision, residency and construction under FM Global (roof NAV 232380)",
          ],
        },
      ],
    },
    achievements: {
      eyebrow: "Key achievements",
      title: "Technical complexity. Strategic delivery.",
      items: [
        {
          k: "2,000-GPM fire-pump engineering, supply and installation",
          v: "Delivered for Ametek Lamb (Plant II) and Unomedical / ConvaTec, covering diesel/electric pumps with jockey systems and combustion engines.",
        },
        {
          k: "Clean-agent protection of power-generation critical areas",
          v: "Novec 1230 total-flood engineering for Iberdrola Ramos Arizpe's electronic rooms, raised floors and control rooms.",
        },
        {
          k: "Deluge protection of turbine and transformer assets",
          v: "Maintenance of deluge systems on gas turbines TG1/TG2, steam turbine, cable basement and trenches at Comego Central Lomas del Real.",
        },
        {
          k: "HDPE thermofusion on 12″ underground fire rings",
          v: "Executed at Ternium Guerrero, Ternium México and Tenaris TAMSA — specialist equipment and qualified personnel.",
        },
        {
          k: "22,500 m² + 2,916 m² high-hazard sprinkler programs",
          v: "Full-scale installations at Alcom Planta MD-3 (Stage 2) and Nissan Aguascalientes (6-riser vehicle assembly).",
        },
        {
          k: "NRF-053-PEMEX-2006 high-temperature coating",
          v: "RE-30A aluminum pigment (3-mil final thickness) on the Burgos naphtha-fractionation plant and storage area.",
        },
        {
          k: "Mechanical-integrity reviews on processing assets",
          v: "Amine treatment columns, stripping columns, horizontal separators and process skids — NDT inspection and risk-based planning.",
        },
        {
          k: "ASME U-stamp pressure-vessel procurement",
          v: "Horizontal three-phase separators and associated pressure-control skids — QA/QC sign-off for field deployment.",
        },
        {
          k: "Inconel 625 Welding Overlay (WOL) on coke drums",
          v: "Structural WOL with temper-bead process (2–4 layers of Inconel 625) delivered at PEMEX Minatitlán refinery coke drums V-31001 / V-31002 / V-31003 — extending equipment life under bulging conditions up to 2.5%.",
        },
        {
          k: "Fabrication of 806 autotankers for PEMEX",
          v: "Large-scale fabrication and delivery of tractor-tank units under DOT/SCT specifications — one of the largest national-scale fleet programs, consolidating Mexican market leadership in autotanker manufacturing.",
        },
        {
          k: "40,000 m² of specialized workshop footprint",
          v: "Integrated fabrication infrastructure (Cadereyta, N.L.): stainless-steel and aluminum plant, LPG/LNG/CNG/cryogenic plant and heavy-equipment plant — 130,000 m² total area.",
        },
      ],
    },
    presence: {
      eyebrow: "Geographical presence",
      title: "Across Mexico, with advisory desks in the United States.",
      items: [
        { k: "Tabasco", v: "Villahermosa · HQ · Engineering & Operations" },
        { k: "Campeche", v: "Ciudad del Carmen · Offshore Field Base" },
        { k: "Nuevo León", v: "Monterrey (Av. Díaz Ordaz, Torre VINTA) — Fabrication & Heavy Equipment HQ · Cadereyta — 40,000 m² specialty workshops (stainless, aluminum, LPG/LNG/CNG/cryogenics, heavy equipment) · San Nicolás de los Garza · Pesquería · Ciénega de Flores" },
        { k: "Mexico City", v: "Torre Magenta — Av. Reforma · Commercial & Advisory Office" },
        { k: "Tamaulipas", v: "Reynosa · Altamira · Valle Hermoso · Ciudad Victoria" },
        { k: "Coahuila", v: "Ramos Arizpe — power-generation assignments" },
        { k: "Aguascalientes", v: "Automotive manufacturing assignments" },
        { k: "Jalisco", v: "Guadalajara — Oil & Gas storage terminals" },
        { k: "Veracruz", v: "Minatitlán · Madero — refinery coke-drum programs" },
        { k: "Texas, USA", v: "Houston (Woodway Tower 4900 Woodway Dr) · Advisory & Procurement Desk" },
      ],
    },
    clients: {
      eyebrow: "Client portfolio",
      title: "A client list built over two decades.",
      groups: [
        {
          name: "Energy & Oil & Gas",
          list: [
            "PEMEX",
            "PEMEX — Minatitlán Refinery",
            "PEMEX — Cadereyta Refinery",
            "PEMEX — Madero Refinery",
            "Nueva Refinería Dos Bocas",
            "Iberdrola Generación México",
            "Comego — Central Anahuac · Lomas del Real · Valle Hermoso",
            "Techgen",
            "MARVIC Tecnologías en Petróleo y Gas",
            "Plains All American Pipeline, L.L.C.",
            "ENERG Group",
            "GASRGL",
            "Grupo Burgos",
            "Petrom",
          ],
        },
        {
          name: "Industrial Gases & Chemicals",
          list: [
            "Air Liquide",
            "Praxair",
            "CEMEX",
            "GARSITE",
          ],
        },
        {
          name: "EPC, Heavy Lift & International Partners",
          list: [
            "Bechtel",
            "Duro Felguera",
            "MAMMOET",
            "US Industrial Services, Inc.",
            "ITT of Italia",
            "Walter Tosto",
            "Hindú TEMA",
            "CSNRI",
            "DeltaValve",
            "HILTI",
            "Piping Technology & Products, Inc.",
            "WYATT",
          ],
        },
        {
          name: "Steel & Heavy Industry",
          list: [
            "Ternium México · Planta Guerrero",
            "Tenaris — Tubos de Acero de México (TAMSA)",
            "Tenigal",
          ],
        },
        {
          name: "Automotive & Manufacturing",
          list: [
            "Nissan Mexicana",
            "Panasonic Automotive Systems",
            "Ametek Lamb Motores de México (Plants I & II)",
            "Alcom Electrónicos de México (Plants MD1 / MD2 / MD3)",
            "Motores Herméticos del Sur",
            "Brunswick Compañías de México",
            "Monroe México",
            "Cornelius Manufacturing de México",
            "Leroy-Somer Maquinaria y Herramientas",
            "Impresora Donneco Internacional",
          ],
        },
        {
          name: "Medical, Healthcare & Ports",
          list: [
            "Unomedical Devices (ConvaTec Company)",
            "Hospital Clínica Nova",
            "Administración Portuaria Integral de Altamira (API)",
          ],
        },
        {
          name: "Education, Aviation & Culture",
          list: [
            "Universidad Autónoma de Tamaulipas (UAT Victoria)",
            "Aeropuerto de Reynosa",
            "Centro Cultural Reynosa",
          ],
        },
        {
          name: "Construction & Industrial Partners",
          list: [
            "Magnum Ingeniería",
            "G3 Construcciones Asociados GB",
            "RC KA Solutions",
            "Multiservicios Industriales y Petroleros GP",
            "Invelmex",
            "IPAMEX AI (Industrial Puerto del Aire)",
            "Integradores de Tecnología",
            "SEA Servicios Especializados Alanis",
            "Interamericas de México",
            "Grupo Ibarra",
            "E3 Constructora Etres",
          ],
        },
      ],
    },
    differentiators: {
      eyebrow: "Differentiators",
      title: "Why clients trust this consolidated capability.",
      items: [
        {
          k: "Single-point accountability across disciplines",
          v: "From engineering to commissioning to life-safety — one partner, one contract, one governance model.",
        },
        {
          k: "International code compliance",
          v: "ASME · API · ISO · AWS · ASNT · NFPA · FM Global, together with Mexican NRFs (NRF-053-PEMEX) and NOMs (NOM-022-STPS).",
        },
        {
          k: "Critical-asset experience",
          v: "Power generation (Iberdrola), steel (Ternium, Tenaris), OEM automotive (Nissan, Panasonic), and medical devices (ConvaTec) — environments where failure is not an option.",
        },
        {
          k: "Emergency and rapid-response posture",
          v: "24/7 operational readiness — crews, rigging and fabrication workshops sized to mobilize within 24 hours.",
        },
        {
          k: "Integrated life-safety practice",
          v: "A dedicated fire-protection and industrial-safety unit that removes the need for a secondary subcontractor on critical projects.",
        },
        {
          k: "Discretion and confidentiality by default",
          v: "Private advisory posture — NDA-ready, sector-sensitive, and built for high-ticket engagements.",
        },
      ],
    },
    footer: {
      note: "This capability statement consolidates project experience across DEPP C&M's operating alliance. All project names, clients and locations are preserved as provided by each contributing entity.",
      request: "Request a full capability deck",
    },
  },

  es: {
    hero: {
      eyebrow: "Declaración de Capacidades · Portafolio",
      title: "Un historial consolidado en ingeniería, servicios industriales y protección contra incendio.",
      subtitle:
        "DEPP C&M, operando en alianza con una red de firmas especializadas, consolida más de dos décadas de experiencia de campo en petróleo y gas, refinación y petroquímica, generación eléctrica, acero, automotriz, puertos, salud e infraestructura industrial — entregada bajo códigos internacionales y marcos ASME, API, NFPA y FM Global.",
      cta_back: "Volver al sitio",
      cta_contact: "Solicitar Asesoría",
      meta: [
        { k: "Años de práctica", v: "18+" },
        { k: "Proyectos entregados", v: "500+" },
        { k: "Clientes atendidos", v: "50+" },
        { k: "Superficie de talleres", v: "40,000 m²" },
      ],
    },
    exec: {
      eyebrow: "Resumen ejecutivo",
      title: "Ingeniería, construcción, inspección y protección contra incendio integrados — bajo un solo socio responsable.",
      body: [
        "DEPP C&M entrega una capacidad consolidada que cubre el ciclo industrial completo: ingeniería conceptual y de detalle, procura, fabricación, construcción en campo, inspección no destructiva, integridad mecánica, comisionamiento y mantenimiento de largo plazo — extendidos con una práctica dedicada a protección contra incendio y seguridad industrial.",
        "Nuestro historial combinado abarca upstream, midstream y downstream de petróleo y gas (programas de refinerías PEMEX, plantas de tratamiento de gas, Refinería Dos Bocas y asignaciones de WOL en Minatitlán), fabricación especializada para refinación y petroquímica (tambores de coque, tanques esféricos, recipientes a presión, reformadores, cambiadores de calor), generación eléctrica (plantas de cogeneración de Iberdrola y Grupo Comego), industria pesada (Ternium, Tenaris TAMSA), manufactura automotriz (Nissan, Panasonic, Ametek), dispositivos médicos (ConvaTec), infraestructura aeroportuaria y cultural, e instalaciones hospitalarias.",
        "Operamos bajo estándares internacionales — ASME (sellos U, U2, R, S, NB), API (510, 570, 571, 579, 580, 650, 653, 2000), ISO 9001/14001/45001, AWS, ASNT (Niveles I–III), DOT 406/407/412/MC33 y NFPA 11/30/780 — y marcos regulatorios mexicanos (NRF-053-PEMEX, NOM-022-STPS), llevando gobernanza de grado institucional a cada asignación.",
      ],
    },
    capabilities: {
      eyebrow: "Capacidades esenciales",
      title: "Siete prácticas integradas. Un solo socio responsable.",
      groups: [
        {
          name: "Ingeniería e Infraestructura",
          items: [
            "Ingeniería conceptual, básica y de detalle",
            "Sistemas de ductos — gas, crudo y condensado",
            "Diseño de procesos · P&IDs · Diagramas de flujo",
            "Obra civil, edificaciones industriales y urbanización",
            "Ingeniería AS-BUILT, cortes y especificación de materiales",
          ],
        },
        {
          name: "Operaciones de Campo y Perforación",
          items: [
            "Instalación y mantenimiento de cabezales de pozo",
            "Válvulas y equipos de control de presión",
            "Swabbing y sistemas de levantamiento artificial",
            "Comisionamiento y soporte de workover",
            "Movilización y rig-up de equipos de perforación",
          ],
        },
        {
          name: "Plataformas Offshore y Embarcaciones",
          items: [
            "Soporte a plataformas jack-up, semisumergibles y drillships",
            "Mantenimiento de plataformas fijas y modulares",
            "Comisionamiento y arranque offshore",
            "Logística marítima y avituallamiento",
          ],
        },
        {
          name: "Mantenimiento Industrial e Inspección",
          items: [
            "Programas de mantenimiento preventivo y correctivo",
            "Pruebas no destructivas (UT · RT · MT · PT) certificadas ASNT",
            "Integridad mecánica e inspección basada en riesgo",
            "Paros de planta y turnaround",
            "Revisión de integridad de recipientes y columnas a presión",
          ],
        },
        {
          name: "Grúas, Izaje y Maniobras",
          items: [
            "Grúas terrestres, HIABs y montacargas certificados",
            "Maniobras de izaje pesado e ingeniería de rigging",
            "Operadores y rigger certificados",
            "Movilización a nivel nacional de paquetes y equipos",
          ],
        },
        {
          name: "Fabricación y Equipo Pesado",
          items: [
            "Recipientes a presión, tanques de almacenamiento e intercambiadores",
            "Sistemas criogénicos",
            "Sistemas de transporte de combustibles",
            "Paquetes skid-mounted de proceso",
            "Recipientes con ASME U-stamp y liberación QA/QC",
          ],
        },
        {
          name: "Fabricación Especializada de Proceso (Refinación y Petroquímica)",
          items: [
            "Tambores de coque — diseño, fabricación y Welding Overlay (WOL) con Inconel 625 vía temper-bead",
            "Tanques esféricos (LPG / GNL / GNC) — diseño, construcción y rehabilitación",
            "Tanques verticales de almacenamiento — fabricación y rehabilitación (API 650 / API 653)",
            "Cambiadores de calor, reformadores y calentadores (crudo, vacío, hidrodesulfuración, reformación, etano)",
            "Fabricación de autotanques / trailer-tanks — DOT 406/407/412, MC33 — más de 4,500 unidades entregadas",
            "Rolado de placa hasta 4″ de espesor · Prensado de tapas hasta 1,500 toneladas · Corte láser/plasma",
            "Soldadura robotizada para overlay · SMAW · SAW · GMAW · GTAW · FCAW · ESW · PAW",
          ],
        },
        {
          name: "Rehabilitación de Plantas y Descontaminación de Proceso",
          items: [
            "Rehabilitación de plantas completas — diagnóstico, procedimientos de reparación, equipo estático y dinámico",
            "Descontaminación de proceso — 60+ proyectos entregados en refinerías mexicanas desde 2008",
            "Limpieza química y recuperación de eficiencia en Packinox / intercambiadores",
            "Servicios de Hot Tapping y Tapping-Machine",
            "Recirculación de alta presión y alto flujo para descontaminación en línea",
            "Servicios de vacío industrial — unidades de 3,037 galones / 15 yardas cúbicas",
            "Refuerzos no metálicos ASME PCC-2",
          ],
        },
        {
          name: "Eléctrica, HVAC y Recubrimientos Especiales",
          items: [
            "Instalaciones eléctricas para instalaciones PEMEX",
            "Instalación de transformadores de potencia y mantenimiento preventivo de subestaciones",
            "HVAC chillers, refrigeración industrial y reacondicionamiento de cuartos limpios",
            "Recubrimientos especiales — sand-blast metal blanco, RP-4, RE-30A (aluminio alta temperatura), 5GP ignífugo retardante, epóxico cromato de zinc",
            "Impermeabilización — sistemas laminares en caliente y membranas en frío",
          ],
        },
        {
          name: "Protección Contra Incendio y Seguridad Industrial",
          items: [
            "Ingeniería (NFPA / FM Global) — estudios de riesgo, cálculos hidráulicos y eléctricos, cálculos antisísmicos, isométricos SCI, DTIs, pruebas de flujo y eficiencia de bombas",
            "Sistemas a base de agua — húmedos · secos · pre-acción · diluvio · agua-espuma · rociadores con anticongelante",
            "Agentes limpios — FM-200, Novec 1230, Ecaro-25, CO₂, Argonite",
            "Bombas contra incendio (hasta 2,000 GPM) — motores diésel, eléctricos, jockey — y construcción de cuartos de bombas",
            "Sistemas de detección y alarma — instalación, puesta en marcha y servicio correctivo",
            "Tubería subterránea (PVC / HDPE termofusión) y pruebas de flujo a hidrantes",
            "Supresión en campanas de cocina — sistemas Amerex y Buckeye",
            "Pólizas de inspección — mensual y anual — a extintores, sistemas fijos, detección y alarmas",
          ],
        },
        {
          name: "HSE, Cumplimiento y Ambiente",
          items: [
            "Programas HSE — cumplimiento OSHA, API, ISO y NOM-022-STPS",
            "Tratamiento de aguas oleosas y recuperación de hidrocarburos",
            "Suministro de personal especializado (ingeniería, campo, QA/QC, HSE)",
            "Administración, logística y avituallamiento para operaciones offshore y remotas",
          ],
        },
      ],
    },
    portfolio: {
      eyebrow: "Portafolio de proyectos",
      title: "Asignaciones seleccionadas — organizadas por sector.",
      subtitle:
        "Una muestra representativa del portafolio combinado de proyectos en los sectores donde DEPP C&M y sus socios de alianza han operado. Todos los nombres de proyecto se conservan tal como fueron entregados.",
      sectors: [
        {
          name: "Petróleo y Gas — Upstream, Midstream y Downstream",
          projects: [
            "Estaciones PEMEX — Instalaciones eléctricas para instalaciones PEMEX",
            "T.A.R. El Castillo Terminal de Almacenamiento — Guadalajara, Jalisco · Obra civil, reposición de firme, obra eléctrica para alimentación y monitoreo de llenaderas",
            "Planta Fraccionadora de Nafta Pesada y Almacenamiento — CPG Burgos · Recubrimiento RE-30A aluminio alta temperatura, espesor final 3 milésimas",
            "Activo Integral Burgos — Ingeniería AS-BUILT, cortes, perspectivas y especificación de materiales para arreglos mecánicos",
            "Torre de Regeneración de Gas — Ingeniería de detalle, supervisión de procura y comisionamiento de columna de regeneración en paquete de tratamiento de gas",
            "Columna de Tratamiento con Amina — Revisión de integridad mecánica, inspección NDT y plan de mantenimiento correctivo (servicio de remoción de H₂S)",
            "Separador Horizontal — Recipiente a Presión — Procura, inspección de fabricación y liberación QA/QC de separador trifásico con paquete de control de presión",
            "Programa de Perforación Onshore — Movilización y rig-up, ensamble de subestructura, drawworks y mesa rotaria, mástiles, control de sólidos, bombas triplex, paquetes de potencia con motogeneradores",
            "Base Offshore en el Golfo de México",
          ],
        },
        {
          name: "Fabricación Especializada — Refinación y Petroquímica",
          projects: [
            "Refinería PEMEX Minatitlán — Welding Overlay (WOL) al Tambor de Coque V-31001 con Inconel 625 vía temper-bead (2–4 capas) — 2016",
            "Refinería PEMEX Minatitlán — Mapeo láser al Tambor V-31002, ovalización y diagnóstico de abultamientos",
            "Refinería PEMEX Minatitlán — Programa de rehabilitación al Tambor de Coque V-31003",
            "PEMEX — Fabricación y entrega de 806 autotanques (tractocamión-tanque) bajo especificaciones DOT/SCT",
            "Nueva Refinería Dos Bocas — Fabricación de tanques verticales de almacenamiento (TV-90400 y campo de tanques asociado)",
            "Refinería Cadereyta / Madero / Minatitlán — Materiales para tambores de coque: 1¼ Cr-Mo, 2¼ Cr-Mo con revestimiento acero inoxidable 410",
            "Air Liquide — Ingeniería de recipiente a presión y cálculos mecánicos para torre de carbón activado",
            "International Epizen — Diseño y construcción de tanques esféricos con sello ASME",
            "Refinerías Mexicanas — 60+ proyectos de descontaminación de proceso desde 2008 (recuperación de eficiencia en Packinox / intercambiadores, prevención de incendios por pirofóricos, control de contaminación atmosférica)",
            "Recipientes a presión entregados en Hastelloy, Inconel, aleaciones de níquel, aleaciones de cobre, aceros de baja aleación, acero inoxidable, acero al carbón y aluminio — incluyendo fraccionadores de crudo, unidades de vacío para convertidores catalíticos, torres de amina y torres de alquilación",
            "Calentadores y Reformadores (colaboración ITT de Italia) — crudo, vacío, hidrodesulfuración, reformación, coque, etano; reformadores de hidrógeno, amoniaco y metanol",
          ],
        },
        {
          name: "Generación Eléctrica",
          projects: [
            "Planta de Cogeneración Iberdrola — Ramos Arizpe, Coahuila · Puesta en marcha del sistema de detección y alarmas",
            "Planta de Cogeneración Iberdrola — Ramos Arizpe · Ingeniería de sistema contra incendio a base de agente limpio Novec 1230 por inundación total en sala electrónica, piso falso, cuarto de control, sala de juntas y sala electrónica",
            "Planta de Cogeneración Iberdrola — Ramos Arizpe · Ingeniería de sistema de bombeo contra incendio",
            "Planta de Cogeneración Iberdrola — Ramos Arizpe · Pruebas hidrostáticas y recarga a tanques de CO₂ de 100 lbs",
            "Comego Central Lomas del Real — Valle Hermoso, Tamaulipas · Mantenimiento a sistemas de diluvio en transformadores principales y auxiliares, turbinas de gas TG1/TG2, turbina de vapor, sótano de cables y trincheras",
            "Comego Central Lomas del Real — Pruebas de eficiencia a bombas con motor diésel eléctrico y jockey; prueba de flujo a sistemas de hidrantes",
            "Comego Central Anahuac — Valle Hermoso · Prueba de flujo a sistemas de hidrantes",
            "Comego Central Valle Hermoso — Prueba de flujo a sistemas de hidrantes",
            "Techgen — Pesquería, Nuevo León · Póliza de inspección, pruebas y mantenimiento a sistemas contra incendio",
          ],
        },
        {
          name: "Acero e Industria Pesada",
          projects: [
            "Ternium Planta Guerrero — San Nicolás de los Garza, NL · Cierre de anillo contra incendio por desmantelamiento (MC1) mediante termofusión HDPE",
            "Ternium Planta Guerrero — Póliza de inspección, pruebas y mantenimiento a boquillas monitoras",
            "Ternium México — San Nicolás de los Garza · Reparaciones de anillos subterráneos contra incendio de 12″ por termofusión",
            "Tenaris TAMSA (Tubos de Acero de México) — Ciénega de Flores, NL · Reparaciones de anillos subterráneos de 12″ por termofusión",
            "Tenigal — Pesquería, NL · Correctivos a sistema contra incendio; reemplazo de válvula de alarma en sistema de rociadores de almacén de residuos peligrosos; póliza de mantenimiento",
          ],
        },
        {
          name: "Automotriz y Manufactura",
          projects: [
            "Alcom Electrónicos de México — Reynosa · Sistemas de rociadores automáticos en MD1/MD2/MD3 (Etapa 1, Etapa 2 — 22,500 m², y Etapa 3); Riser 2.2, Riser 3, Riser 6; tubería subterránea PVC uniendo anillo principal con sistema de bombeo; sistema de diluvio agua-espuma para almacén de químicos; mantenimiento preventivo de CO₂ en 11 cabinas de pintura; medición de flujo en Chiller; rociadores en área de moldeo",
            "Panasonic Automotive Systems Reynosa — Póliza de inspección y mantenimiento mayor a bomba contra incendio y motor de combustión",
            "Ametek Lamb Motores de México Planta I y II — Reynosa · Ingeniería e instalación de bomba 2,000 GPM; rociadores en almacén Villa Florida y Power & Switch; mantenimiento interno a bomba centrífuga; póliza de inspección mensual",
            "Motores Herméticos del Sur — Reynosa · Sistema de agente limpio para 7 celdas de fundición con detección por zona cruzada; supresión en campanas de Die Cast (celdas 3/4/5/9); recarga FM-200; mantenimiento preventivo a torre de enfriamiento, chillers y cisternas",
            "Nissan Mexicana Planta A1 — Aguascalientes · Sistema de rociadores automáticos para área de ensamble de vehículos (6 risers, 2,916 m²)",
            "Monroe México — Reynosa · Póliza anual de recargas y mantenimiento a extintores",
            "Cornelius Manufacturing de México — Reynosa · Sistema de protección para cocina industrial Buckeye",
            "Brunswick Compañías de México — Reynosa · Construcción de edificio para moldes bajo FM Global; protección contra incendio en cuarto de espumado, área de techado sur y mantenimiento de moldes; rociadores con anticongelante en techumbre sur, cuarto de químicos y área de moldeo",
            "Leroy-Somer — Reynosa · Correctivo de 78 fallas en sistema de detección y alarmas; restauración a operación normal",
            "Impresora Donneco Internacional — Reynosa · Preventivos y correctivos a tanque atornillado; mantenimiento interno a bomba centrífuga",
          ],
        },
        {
          name: "Puertos e Infraestructura Marítima",
          projects: [
            "Administración Portuaria Integral de Altamira (API Altamira) — Sistema de agente limpio Ecaro-25 a site",
            "Administración Portuaria Integral de Altamira (API Altamira) — Supresión en campana de cocina Amerex",
          ],
        },
        {
          name: "Salud y Dispositivos Médicos",
          projects: [
            "Unomedical Devices — ConvaTec — Reynosa · Ingeniería e instalación de bomba 2,000 GPM; póliza de inspección mensual a sistemas fijos y detección/alarmas",
            "Hospital Clínica Nova — San Nicolás de los Garza · Sistema de detección y alarmas en laboratorio, rayos X y rampas N1/N2/N3",
            "Unidad Médica Familiar Prados de Alcalá — Aplicación de recubrimiento ignífugo 5GP (3 horas al fuego) en estructura metálica",
          ],
        },
        {
          name: "Educación e Instituciones Públicas",
          projects: [
            "Universidad Autónoma de Tamaulipas — UAT Victoria · Mantenimiento preventivo a doce sistemas FM-200 en Biblioteca Principal",
          ],
        },
        {
          name: "Aviación e Infraestructura Cultural",
          projects: [
            "Nuevo Edificio del Aeropuerto — Reynosa · Instalaciones eléctricas",
            "Centro Cultural Reynosa — Instalaciones hidrosanitarias y construcción de mezzanine",
          ],
        },
        {
          name: "Construcción Industrial (cumplimiento FM-Global)",
          projects: [
            "Edificio para Moldes — Parque del Puente, Brunswick, Reynosa · Supervisión, residencia y construcción bajo FM Global (roof NAV 232380)",
          ],
        },
      ],
    },
    achievements: {
      eyebrow: "Logros clave",
      title: "Complejidad técnica. Entrega estratégica.",
      items: [
        {
          k: "Ingeniería, suministro e instalación de bombas contra incendio de 2,000 GPM",
          v: "Entregada para Ametek Lamb (Planta II) y Unomedical / ConvaTec, cubriendo bombas diésel/eléctricas con sistemas jockey y motores de combustión.",
        },
        {
          k: "Protección con agente limpio en áreas críticas de generación eléctrica",
          v: "Ingeniería Novec 1230 por inundación total para salas electrónicas, pisos falsos y cuartos de control de Iberdrola Ramos Arizpe.",
        },
        {
          k: "Protección por diluvio a turbinas y transformadores",
          v: "Mantenimiento a diluvio en turbinas de gas TG1/TG2, turbina de vapor, sótano de cables y trincheras en Comego Central Lomas del Real.",
        },
        {
          k: "Termofusión HDPE en anillos subterráneos de 12″",
          v: "Ejecutada en Ternium Guerrero, Ternium México y Tenaris TAMSA — equipo especializado y personal calificado.",
        },
        {
          k: "Programas de rociadores de alto riesgo de 22,500 m² + 2,916 m²",
          v: "Instalaciones a escala en Alcom Planta MD-3 (Etapa 2) y Nissan Aguascalientes (6 risers en ensamble de vehículos).",
        },
        {
          k: "Recubrimiento de alta temperatura NRF-053-PEMEX-2006",
          v: "Pigmento de aluminio RE-30A (espesor final 3 milésimas) en planta fraccionadora de nafta y almacenamiento Burgos.",
        },
        {
          k: "Revisiones de integridad mecánica en activos de proceso",
          v: "Columnas de tratamiento con amina, columnas de despojamiento, separadores horizontales y paquetes de proceso — inspección NDT y planificación basada en riesgo.",
        },
        {
          k: "Procura de recipientes con ASME U-stamp",
          v: "Separadores trifásicos horizontales y paquetes de control de presión asociados — liberación QA/QC para despliegue en campo.",
        },
        {
          k: "Welding Overlay (WOL) en Inconel 625 sobre tambores de coque",
          v: "WOL estructural con proceso temper-bead (2–4 capas de Inconel 625) entregado en los tambores de coque V-31001 / V-31002 / V-31003 de la refinería PEMEX Minatitlán — extendiendo la vida del equipo bajo condiciones de abultamiento de hasta 2.5%.",
        },
        {
          k: "Fabricación de 806 autotanques para PEMEX",
          v: "Fabricación y entrega a gran escala de unidades tractocamión-tanque bajo especificaciones DOT/SCT — uno de los programas de flota a escala nacional más grandes, consolidando el liderazgo del mercado mexicano en manufactura de autotanques.",
        },
        {
          k: "40,000 m² de talleres especializados",
          v: "Infraestructura integrada de fabricación (Cadereyta, N.L.): planta de acero inoxidable y aluminio, planta LPG/GNL/GNC/criogénicos y planta de equipo pesado — 130,000 m² de área total.",
        },
      ],
    },
    presence: {
      eyebrow: "Presencia geográfica",
      title: "A lo largo de México, con mesas de asesoría en Estados Unidos.",
      items: [
        { k: "Tabasco", v: "Villahermosa · Matriz · Ingeniería y Operaciones" },
        { k: "Campeche", v: "Ciudad del Carmen · Base Offshore" },
        { k: "Nuevo León", v: "Monterrey (Av. Díaz Ordaz, Torre VINTA) — Matriz de Fabricación y Equipo Pesado · Cadereyta — 40,000 m² de talleres especializados (inoxidable, aluminio, LPG/GNL/GNC/criogénicos, equipo pesado) · San Nicolás de los Garza · Pesquería · Ciénega de Flores" },
        { k: "Ciudad de México", v: "Torre Magenta — Av. Reforma · Oficina Comercial y de Asesoría" },
        { k: "Tamaulipas", v: "Reynosa · Altamira · Valle Hermoso · Ciudad Victoria" },
        { k: "Coahuila", v: "Ramos Arizpe — asignaciones de generación eléctrica" },
        { k: "Aguascalientes", v: "Asignaciones automotrices" },
        { k: "Jalisco", v: "Guadalajara — Terminales de almacenamiento" },
        { k: "Veracruz", v: "Minatitlán · Madero — programas de tambores de coque en refinerías" },
        { k: "Texas, EE.UU.", v: "Houston (Woodway Tower 4900 Woodway Dr) · Mesa de Asesoría y Procura" },
      ],
    },
    clients: {
      eyebrow: "Portafolio de clientes",
      title: "Una lista de clientes construida en dos décadas.",
      groups: [
        {
          name: "Energía y Petróleo/Gas",
          list: [
            "PEMEX",
            "PEMEX — Refinería Minatitlán",
            "PEMEX — Refinería Cadereyta",
            "PEMEX — Refinería Madero",
            "Nueva Refinería Dos Bocas",
            "Iberdrola Generación México",
            "Comego — Central Anahuac · Lomas del Real · Valle Hermoso",
            "Techgen",
            "MARVIC Tecnologías en Petróleo y Gas",
            "Plains All American Pipeline, L.L.C.",
            "ENERG Group",
            "GASRGL",
            "Grupo Burgos",
            "Petrom",
          ],
        },
        {
          name: "Gases Industriales y Químicos",
          list: [
            "Air Liquide",
            "Praxair",
            "CEMEX",
            "GARSITE",
          ],
        },
        {
          name: "EPC, Izaje Pesado y Socios Internacionales",
          list: [
            "Bechtel",
            "Duro Felguera",
            "MAMMOET",
            "US Industrial Services, Inc.",
            "ITT de Italia",
            "Walter Tosto",
            "Hindú TEMA",
            "CSNRI",
            "DeltaValve",
            "HILTI",
            "Piping Technology & Products, Inc.",
            "WYATT",
          ],
        },
        {
          name: "Acero e Industria Pesada",
          list: [
            "Ternium México · Planta Guerrero",
            "Tenaris — Tubos de Acero de México (TAMSA)",
            "Tenigal",
          ],
        },
        {
          name: "Automotriz y Manufactura",
          list: [
            "Nissan Mexicana",
            "Panasonic Automotive Systems",
            "Ametek Lamb Motores de México (Plantas I y II)",
            "Alcom Electrónicos de México (Plantas MD1 / MD2 / MD3)",
            "Motores Herméticos del Sur",
            "Brunswick Compañías de México",
            "Monroe México",
            "Cornelius Manufacturing de México",
            "Leroy-Somer Maquinaria y Herramientas",
            "Impresora Donneco Internacional",
          ],
        },
        {
          name: "Salud, Médico y Puertos",
          list: [
            "Unomedical Devices (ConvaTec Company)",
            "Hospital Clínica Nova",
            "Administración Portuaria Integral de Altamira (API)",
          ],
        },
        {
          name: "Educación, Aviación y Cultura",
          list: [
            "Universidad Autónoma de Tamaulipas (UAT Victoria)",
            "Aeropuerto de Reynosa",
            "Centro Cultural Reynosa",
          ],
        },
        {
          name: "Socios de Construcción e Industria",
          list: [
            "Magnum Ingeniería",
            "G3 Construcciones Asociados GB",
            "RC KA Solutions",
            "Multiservicios Industriales y Petroleros GP",
            "Invelmex",
            "IPAMEX AI (Industrial Puerto del Aire)",
            "Integradores de Tecnología",
            "SEA Servicios Especializados Alanis",
            "Interamericas de México",
            "Grupo Ibarra",
            "E3 Constructora Etres",
          ],
        },
      ],
    },
    differentiators: {
      eyebrow: "Diferenciadores",
      title: "Por qué los clientes confían en esta capacidad consolidada.",
      items: [
        {
          k: "Un solo punto de responsabilidad entre disciplinas",
          v: "De ingeniería a comisionamiento a protección contra incendio — un socio, un contrato, un modelo de gobernanza.",
        },
        {
          k: "Cumplimiento de códigos internacionales",
          v: "ASME · API · ISO · AWS · ASNT · NFPA · FM Global, junto con NRFs mexicanas (NRF-053-PEMEX) y NOMs (NOM-022-STPS).",
        },
        {
          k: "Experiencia en activos críticos",
          v: "Generación eléctrica (Iberdrola), acero (Ternium, Tenaris), OEM automotriz (Nissan, Panasonic) y dispositivos médicos (ConvaTec) — entornos donde la falla no es opción.",
        },
        {
          k: "Postura de emergencia y respuesta rápida",
          v: "Disponibilidad operativa 24/7 — cuadrillas, equipos de izaje y talleres dimensionados para movilizarse en menos de 24 horas.",
        },
        {
          k: "Práctica integrada de protección contra incendio",
          v: "Unidad dedicada de protección contra incendio y seguridad industrial que elimina la necesidad de un subcontratista secundario en proyectos críticos.",
        },
        {
          k: "Discreción y confidencialidad por defecto",
          v: "Postura de asesoría privada — NDA-ready, sensibilidad sectorial, construido para proyectos high-ticket.",
        },
      ],
    },
    footer: {
      note: "Esta declaración de capacidades consolida la experiencia de proyectos a lo largo de la alianza operativa de DEPP C&M. Todos los nombres de proyecto, clientes y ubicaciones se conservan tal como fueron aportados por cada entidad contribuyente.",
      request: "Solicitar presentación completa de capacidades",
    },
  },
};
